import { useCallback, useMemo, useState } from 'react';
import { UserAddressDrawerContext, type AddressViews } from './context';
import { useForm } from 'react-hook-form';
import { userAddressSchema, type UserAddressSchema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Coordinates } from '@/modules/location/components/ui/map';

type UserAddressDrawerProviderProps = {
  children: React.ReactNode;
  defaultView?: Exclude<AddressViews, 'first'>;
};

export function UserAddressDrawerProvider({
  children,
  defaultView = 'select-delivery-address',
}: UserAddressDrawerProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [stackView, setStackView] = useState<Exclude<AddressViews, 'first'>[]>([defaultView]);

  // Estado para manter a posição selecionada no mapa
  const [selectedMapPosition, setSelectedMapPosition] = useState<Coordinates | null>(null);

  // Modo de entrada manual (usuario clicou "digitar manualmente")
  const [isManualEntry, setIsManualEntry] = useState(false);

  const formMethods = useForm<UserAddressSchema>({
    resolver: zodResolver(userAddressSchema),
  });

  const { watch, reset } = formMethods;

  const editedAddressId = watch('edit.address_id');

  const navigate = useCallback((view: AddressViews) => {
    if (view === 'first') {
      setStackView(prev => [prev[0]]);
      return;
    }
    setStackView(prev => {
      // Não permitir navegar para a mesma tela
      if (view === prev[prev.length - 1]) return prev;
      return [...prev, view];
    });
  }, []);

  const goBack = useCallback(() => {
    setStackView(prev => prev.slice(0, -1));
  }, []);

  const isEditing = useMemo(() => {
    return !!editedAddressId;
  }, [editedAddressId]);

  const currentView = useMemo(() => stackView[stackView.length - 1], [stackView]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSelectedMapPosition(null);
    setIsManualEntry(false);
    reset();
    setStackView([defaultView]);
  }, [reset, defaultView]);

  const handleBack = useCallback(() => {
    setStackView(prev => {
      if (prev.length === 1) {
        // Fechar drawer
        setIsOpen(false);
        setSelectedMapPosition(null);
        setIsManualEntry(false);
        reset();
        return [defaultView];
      }

      const nextStack = prev.slice(0, -1);
      const targetView = nextStack[nextStack.length - 1];

      // RN: Se a tela que vai ser navegada no goBack é a tela de seleção de endereço, reseta o formulário
      if (targetView === 'select-delivery-address') {
        setIsManualEntry(false);
        reset();
      }

      return nextStack;
    });
  }, [reset, defaultView]);

  const open = useCallback(() => setIsOpen(true), []);

  const contextValue = useMemo(() => ({
    close,
    open,
    isOpen,
    navigate,
    goBack,
    handleBack,
    currentView,
    formMethods,
    selectedMapPosition,
    setSelectedMapPosition,
    isEditing,
    isManualEntry,
    setIsManualEntry,
  }), [close, open, isOpen, navigate, goBack, handleBack, currentView, formMethods, selectedMapPosition, isEditing, isManualEntry]);

  return (
    <UserAddressDrawerContext.Provider value={contextValue}>
      {children}
    </UserAddressDrawerContext.Provider>
  );
}
